import _extends from '@babel/runtime/helpers/extends';
import React__default, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import { C as CardStyled, a as CardBodyStyled } from '../Card/Card.styles-DDgDkg5u.js';
import { W as Wrapper, T as TabGroupWrapper, a as TabGroup, b as TabRemainSpace, c as Tab, d as TabWrapper, D as DefaultText } from './TabbedCard.styles-D9r1Bauq.js';
import { s as scrollIntoBounds } from '../LocalModules/storm-ui-utils/dist/esm/scrollIntoBounds/scrollIntoBounds-Dhl2O567-Dhl2O567.js';
import { T as TaktIdProvider, a as TaktIdConsumer } from '../TaktIdContext/TaktIdContext-C6vdL-6Z.js';
import { c as createStormTaktId } from '../TaktIdContext/TaktIdContext.utils-B0CzqlN8.js';
import { K as KEYBOARD_KEYNAMES } from '../LocalModules/storm-ui-utils/dist/esm/keyboardKeynames-AmBipDtf-AmBipDtf.js';
import { g as getFocusableElements } from '../LocalModules/storm-ui-utils/dist/esm/focusableElements/getFocusableElements-BjFtn5aB-BjFtn5aB.js';

const TabContent = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-storm-ui-40019274__sc-1g69jaq-0"
})(["visibility:", ";height:", ";"], ({
  $hidden
}) => $hidden ? 'hidden' : 'visible', ({
  $hidden
}) => $hidden ? '0' : 'auto');
// Used to add default text wrapper
// or isActive prop to custom components
const modifyTabChildren = (tabChildren, isActive = false) => {
  if (typeof tabChildren === 'string') {
    return /*#__PURE__*/React__default.createElement(DefaultText, {
      color: "inherit",
      $label: tabChildren,
      weight: isActive ? 'bold' : 'regular'
    }, tabChildren);
  }
  if (/*#__PURE__*/React__default.isValidElement(tabChildren)) {
    const {
      type
    } = tabChildren;

    // WARNING...SMELLY CODE AHEAD
    if (
    // is a function component
    /createElement/.test(String(type))

    // is a class component
    || typeof type === 'function' && /react.element/.test(String(tabChildren.$$typeof))) {
      return /*#__PURE__*/React__default.cloneElement(tabChildren, {
        isActive
      });
    }
  }

  // don't modify if custom element is a standard dom element
  return tabChildren;
};
// React component representing the physical tab and its logic
const TabbedCardTabItem = ({
  onChange,
  isActive = false,
  tabChildren,
  value,
  taktId,
  taktValue,
  ...itemProps
}) => {
  const handleKeyUp = event => {
    const {
      key
    } = event;
    if (key === KEYBOARD_KEYNAMES.ENTER || key === KEYBOARD_KEYNAMES.SPACE) {
      onChange(value, event);
      event.preventDefault();
    }
  };
  const handleChange = event => {
    onChange(value, event);
  };
  return /*#__PURE__*/React__default.createElement(TaktIdConsumer, {
    taktId: taktId,
    taktValue: taktValue,
    fallbackId: createStormTaktId('tabbed-card-tab-item')
  }, ({
    getDataTaktAttributes
  }) => /*#__PURE__*/React__default.createElement(Tab, _extends({}, getDataTaktAttributes(), {
    type: "button",
    key: value,
    id: value,
    isActive: isActive,
    "aria-selected": isActive,
    role: "tab",
    tabIndex: isActive ? 0 : -1,
    onClick: handleChange,
    onKeyUp: handleKeyUp
  }, itemProps), /*#__PURE__*/React__default.createElement(TabWrapper, {
    $isActive: isActive
  }, modifyTabChildren(tabChildren, isActive))));
};
const TabbedCard = ({
  children,
  collapseHeaderWithOneTab = false,
  navChildren = null,
  selectedTab,
  onChange,
  padding = 'xsmall',
  preventUnmountOnTabChange = false,
  taktId,
  taktValue,
  ...rest
}) => {
  const navRef = useRef(null);
  const tabGroupRef = useRef(null);
  const [isTabOverflow, setIsTabOverflow] = useState(false);
  useEffect(() => {
    var _tabGroupRef$current, _tabGroupRef$current2, _navRef$current;
    const tabsWidth = (_tabGroupRef$current = tabGroupRef.current) === null || _tabGroupRef$current === void 0 ? void 0 : _tabGroupRef$current.scrollWidth;
    const tabsContainerWidth = (_tabGroupRef$current2 = tabGroupRef.current) === null || _tabGroupRef$current2 === void 0 ? void 0 : _tabGroupRef$current2.offsetWidth;

    /* Set a isTabOverflow flag on whether to add the scroll indicator */
    if (tabsWidth && tabsContainerWidth && tabsWidth > tabsContainerWidth) {
      setIsTabOverflow(true);
    }
    /* Scroll the active tab to be visible in the frame if required. */
    scrollIntoBounds(navRef.current, (_navRef$current = navRef.current) === null || _navRef$current === void 0 ? void 0 : _navRef$current.querySelector('[aria-selected=true][role="tab"]'));
  }, []);
  const handleKeyDown = event => {
    const {
      key
    } = event;

    // By default, buttons handle the Enter key on KeyDown. We prevent this because we want
    // to listen to KeyUp to prevent repeat triggers.
    if (key === KEYBOARD_KEYNAMES.ENTER) {
      event.preventDefault();
    }
    if (tabGroupRef.current) {
      const focusableEls = getFocusableElements(tabGroupRef.current);
      const firstFocusableEl = focusableEls[0];
      const lastFocusableEl = focusableEls[focusableEls.length - 1];

      // When "Left Arrow" or "Right Arrow" is pressed, move focus to the left or right tab
      if ((key === KEYBOARD_KEYNAMES.ARROW_RIGHT || key === KEYBOARD_KEYNAMES.ARROW_LEFT || key === KEYBOARD_KEYNAMES.HOME || key === KEYBOARD_KEYNAMES.END) && document.activeElement && document.activeElement instanceof HTMLElement) {
        const currentIndex = focusableEls.indexOf(document.activeElement);
        let newFocusIndex = currentIndex;

        // When "Right Arrow" is pressed, focus on the tab to the right
        if (key === KEYBOARD_KEYNAMES.ARROW_RIGHT) {
          newFocusIndex = currentIndex + 1;
          if (document.activeElement === lastFocusableEl) {
            newFocusIndex = 0;
          }
        }

        // When "Left Arrow" is pressed, focus on the tab to the left
        if (key === KEYBOARD_KEYNAMES.ARROW_LEFT) {
          newFocusIndex = currentIndex - 1;
          if (document.activeElement === firstFocusableEl) {
            newFocusIndex = focusableEls.length - 1;
          }
        }

        // When "End" is pressed, focus on the last tab in the tab group
        if (key === KEYBOARD_KEYNAMES.END) {
          newFocusIndex = focusableEls.length - 1;
        }

        // When "Home" is pressed, focus on the first tab in the tab group
        if (key === KEYBOARD_KEYNAMES.HOME) {
          newFocusIndex = 0;
        }
        focusableEls[newFocusIndex].focus();
        event.preventDefault();
      }
    }
  };
  const tabs = React__default.Children.map(children, child => {
    if (! /*#__PURE__*/React__default.isValidElement(child)) {
      return null;
    }
    const {
      value,
      tabChildren,
      children: itemChildren,
      ...itemRest
    } = child.props;
    const isActive = selectedTab === value;
    return /*#__PURE__*/React__default.createElement(TabbedCardTabItem, _extends({
      onChange: onChange,
      isActive: isActive,
      tabChildren: tabChildren,
      value: value
    }, itemRest));
  });
  const collpaseHeader = collapseHeaderWithOneTab && tabs && tabs.length < 2;
  return /*#__PURE__*/React__default.createElement(TaktIdProvider, {
    taktId: taktId,
    taktValue: taktValue,
    fallbackId: createStormTaktId('tabbed-card')
  }, /*#__PURE__*/React__default.createElement(CardStyled, rest, !collpaseHeader && /*#__PURE__*/React__default.createElement(Wrapper, {
    isTabOverflow: isTabOverflow
  }, /*#__PURE__*/React__default.createElement(TabGroupWrapper, {
    ref: navRef
  }, /*#__PURE__*/React__default.createElement(TabGroup, {
    onKeyDown: handleKeyDown,
    ref: tabGroupRef,
    role: "tablist"
  }, tabs, navChildren, /*#__PURE__*/React__default.createElement(TabRemainSpace, null)))), /*#__PURE__*/React__default.createElement(CardBodyStyled, {
    $padding: padding
  }, React__default.Children.map(children, child => {
    if (! /*#__PURE__*/React__default.isValidElement(child)) {
      return null;
    }
    const {
      value
    } = child.props;
    // return null if content is not selected & should not be preserved on tab change
    if (selectedTab !== value && !preventUnmountOnTabChange) {
      return null;
    }
    return /*#__PURE__*/React__default.createElement(TabContent, {
      key: value,
      $hidden: selectedTab !== value
    }, child);
  }))));
};

export { TabbedCard as T };
//# sourceMappingURL=TabbedCard-Dn41Q5Xh.js.map
